import React, { useEffect, useId, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { getFooterPortal } from 'volto-ministerio-publico-federal/actions';

import LogoSVG from 'volto-ministerio-publico-federal/components/Logo/LogoSVG';
import {
  MPFButton,
  MPFLink,
} from 'volto-ministerio-publico-federal/components/UI';

import {
  ArrowRightIcon,
  CookieIcon,
  MapTrifoldIcon,
} from '@phosphor-icons/react';

export interface FooterLink {
  url: string;
  title: string;
  description?: string;
}

export interface FooterChannelLink {
  title: string;
  url: string;
}

export interface FooterChannel {
  title: string;
  links?: FooterChannelLink[];
}

export interface FooterItems {
  links?: FooterLink[];
  channels?: FooterChannel[];
  showLogo?: boolean;
}

export type FooterPortalState = {
  error: unknown;
  items: FooterItems;
  loaded: boolean;
  loading: boolean;
};

export type RootState = {
  footerPortal: FooterPortalState;
};

/**
 * Ícones dos links da faixa superior, resolvidos pelo título do link
 * (normalizado: minúsculo e sem acentos).
 */
const LINK_ICONS: Record<string, React.ElementType> = {
  'mapa do site': MapTrifoldIcon,
  'gestao de cookies': CookieIcon,
};

const normalizeTitle = (title: string = ''): string =>
  title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const Footer = () => {
  const dispatch = useDispatch();
  const channelsIdPrefix = useId();

  const footerPortalState = useSelector(
    (state: RootState) => state.footerPortal,
  );

  // O ano depende do relógio/fuso da máquina: calcular no render quebraria a
  // paridade SSR/cliente na virada do ano. Só é definido após a montagem.
  const [year, setYear] = useState<number | null>(null);

  useEffect(() => {
    setYear(new Date().getFullYear());
  }, []);

  // O `footerPortal` chega expandido na resposta do conteúdo e o reducer já o
  // consome no `GET_CONTENT_SUCCESS`; buscar de novo repetiria, em toda
  // página, um dado que já está na store. O fetch avulso fica como plano B
  // para quando o expander não vier (por exemplo numa rota de erro).
  const footerLoaded = footerPortalState?.loaded;

  useEffect(() => {
    if (footerLoaded) return;
    dispatch(getFooterPortal());
  }, [dispatch, footerLoaded]);

  const footerData: FooterItems = footerPortalState?.items ?? {};
  const links = footerData.links ?? [];
  const channels = footerData.channels ?? [];
  const showLogo = footerData.showLogo !== false;

  return (
    <footer id="footer" tabIndex={-1} className="mpf-footer-global">
      <div className="mpf-footer-top">
        <div className="mpf-footer-top__container">
          {showLogo ? (
            <div className="mpf-footer-top__brand">
              <LogoSVG className="mpf-footer-top__logo" />
            </div>
          ) : null}
          <div className="mpf-footer-top__nav">
            <nav
              className="mpf-footer-top__links"
              aria-label="Links institucionais"
            >
              {links.map((link, index) => {
                const Icon = LINK_ICONS[normalizeTitle(link.title)];
                // O divisor é irmão do link, não um pseudo-elemento dele: o
                // vão entre os links vem do `justify-between` e muda com a
                // largura da faixa, então só sendo um item do flex ele recebe
                // metade do vão de cada lado e fica centralizado entre os dois
                // vizinhos. `index > 0` evita um divisor solto na abertura.
                const separator = Icon && index > 0;

                return (
                  <React.Fragment key={`${link.url}-${index}`}>
                    {separator ? (
                      <span
                        className="mpf-footer-top__separator"
                        aria-hidden="true"
                      />
                    ) : null}
                    <MPFLink
                      href={link.url}
                      className="mpf-footer-link mpf-footer-link-hover"
                    >
                      {Icon ? (
                        <span
                          className="mpf-footer-link__icon"
                          aria-hidden="true"
                        >
                          <Icon size={24} />
                        </span>
                      ) : null}
                      {link.title}
                    </MPFLink>
                  </React.Fragment>
                );
              })}
            </nav>
          </div>
        </div>
      </div>
      <div className="mpf-footer-middle">
        <div className="mpf-footer-middle__container">
          {channels.map((channel, index) => {
            const titleId = `${channelsIdPrefix}-channel-${index}`;

            return (
              <nav
                className="mpf-footer-middle-grid"
                key={`${channel.title}-${index}`}
                aria-labelledby={titleId}
              >
                <h2 className="mpf-footer-title" id={titleId}>
                  {channel.title}
                </h2>

                <ul className="mpf-footer-channel-links">
                  {channel.links?.map((link, linkIndex) => (
                    <li key={`${link.url}-${linkIndex}`}>
                      <MPFButton
                        as={MPFLink}
                        href={link.url}
                        variant="unstyled"
                        className="mpf-footer-channel-button"
                      >
                        {link.title}
                        <span
                          className="mpf-footer-channel-button__arrow"
                          aria-hidden="true"
                        >
                          <ArrowRightIcon size={20} weight="bold" />
                        </span>
                      </MPFButton>
                    </li>
                  ))}
                </ul>
              </nav>
            );
          })}
        </div>
      </div>
      <div className="mpf-footer-bottom">
        <div className="mpf-footer-bottom__container">
          <p className="mpf-footer-copyright">
            &copy;{' '}
            {year !== null ? (
              <span className="mpf-footer-copyright__year">{year} </span>
            ) : null}
            Ministério Público Federal
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
